import React, { useState, useMemo } from "react";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  BusinessCenterOutlined as BusinessCenterIcon,
  LaptopOutlined as LaptopIcon,
  SettingsOutlined as SettingsIcon,
  Lock as LockIcon,
  ExpandLess,
  ExpandMore,
  SubscriptionsOutlined,
} from "@mui/icons-material";
import {
  FiFileText,
  FiBarChart2,
  FiShoppingCart,
  FiUsers,
  FiBriefcase,
  FiGlobe,
} from "react-icons/fi";
import { MdOutlineDashboard } from "react-icons/md";
import { BiBuilding } from "react-icons/bi";
import { styled } from "@mui/system";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import Lockable from "../Lockable/Lockable";
import { isJwtExpired } from "../../utils/functions";

// import sideNavBG from "../../assets/newNavBanII.png";
import SubscriptionInfo from "./SubscriptionInfo";
import { BadgeIcon, OpenBookIcon, PersonSupportIcon } from "../Icons";

const StyledSideNav = styled(Box)(() => ({
  "& *": {
    fontFamily: "Galano Grotesque Alt !important",
  },
  height: "100%",
  flexShrink: 0,
  backgroundColor: "#fff",
  position: "fixed",
  overflowY: "auto",
  overflowX: "hidden",
  zIndex: "500",
  padding: "1rem 0 5rem 0",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  transition: "width 0.3s",
  border: "1px solid #e9eaeb",
  borderWidth: "0 1px 0 0",
}));
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.neutral.light,
  padding: theme.spacing(1, 3.78),
  marginTop: theme.spacing(2),
  fontSize: "0.625rem",
  textTransform: "uppercase",
}));
const StyledListItem = styled(ListItem)(({ theme, isopen }) => ({
  width: "auto",
  margin: isopen ? "0 16px 5px 16px" : "0 4px 5px 11px",
  paddingTop: "8px",
  paddingBottom: "8px",
  paddingLeft: isopen ? theme.spacing(2) : "10px",
  paddingRight: 0,
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: theme.palette.secondary.main,
    "& .MuiListItemIcon-root": {
      color: theme.palette.paper.main,
    },
    "& .MuiListItemText-root": {
      color: theme.palette.paper.main,
    },
    "& .MuiBox-root": {
      color: theme.palette.paper.main,
    },
  },
  ".MuiBox-root": {
    color: theme.palette.neutral.main,
  },
}));
const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  "& .MuiTypography-root": {
    fontSize: "1rem",
    fontWeight: 500,
  },
}));

const AgentLeftNavNew = ({ onClose }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const type = useSelector((state) => state.auth.role);
  const email = useSelector((state) => state.auth.email);
  const token = useSelector((state) => state.auth.token.refresh);
  const atoken = useSelector((state) => state.auth.token.access); //only access token will work for tranining center
  const { premierAgentWebsite, dealAnalyzer, liveLeads, bpoFarm } = useSelector(
    (state) => state.subscriptions
  );
  const isOpen = useSelector((state) => !state.sidebar.isOpen);

  const [isLoading, setIsLoading] = useState(false);
  const [openCollapsible, setOpenCollapsible] = useState({});

  const handleCollapsibleClick = (id) => {
    setOpenCollapsible((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleClick = async () => {
    if (!(premierAgentWebsite || dealAnalyzer || liveLeads || bpoFarm)) return;
    const url = process.env.REACT_APP_MARKETING_CENTER_URL;
    try {
      setIsLoading(true);
      const { data } = await axios.post(url, {
        token,
      });

      const frontendUrl = data?.data?.frontendUrl;
      const tokenToPass = data?.data?.token.access;

      window.open(
        `${frontendUrl}login/${tokenToPass}?email=${encodeURIComponent(email)}`,
        "_blank"
      );
    } catch (error) {
      toast.warn(error?.response?.data?.error || "An error occurred", {
        toastId: "errrror",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMBAClick = async () => {
    if (!(premierAgentWebsite || dealAnalyzer || liveLeads || bpoFarm)) return;

    const url = process.env.REACT_APP_MBA_BUSINESS_PLAN_URL;
    try {
      setIsLoading(true);
      const { data } = await axios.post(url, {
        token,
      });

      const frontendUrl = data?.data?.frontendUrl;
      const tokenToPass = data?.data?.token.access;
      const urlS = `${frontendUrl}/${tokenToPass}?email=${encodeURIComponent(
        email
      )}`;
      window.open(urlS, "_blank");
    } catch (error) {
      toast.warn(error?.response?.data?.error || "An error occurred", {
        toastId: "errrror",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBroker = () => {
    if (!(premierAgentWebsite || dealAnalyzer || liveLeads || bpoFarm)) return;

    try {
      const url = `${process.env.REACT_APP_BROKER_CENTER}?token=${atoken}`;
      const isExpired = isJwtExpired(atoken);
      if (isExpired) {
        dispatch({ type: "LOGOUT" });
        return;
      }
      window.open(url, "_blank");
    } catch (error) {
      toast.warn(error?.response?.data?.error || "An error occurred", {
        toastId: "errrror",
      });
    }
  };

  const handleTraining = (condition) => {
    if (condition === false) return;

    try {
      const brokerCenter =
        process.env.REACT_APP_TRAINING_CENTER ??
        "https://testing-training.bpotech.io";

      const url = `${brokerCenter}?token=${atoken}`;
      const isExpired = isJwtExpired(atoken);
      if (isExpired) {
        dispatch({ type: "LOGOUT" });
        return;
      }

      window.open(url, "_blank");
    } catch (error) {
      toast.warn(error?.response?.data?.error || "An error occurred", {
        toastId: "errrror",
      });
    }
  };

  const menuItems = useMemo(() => {
    return [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: MdOutlineDashboard,
        path: "agent-dashboard",
      },
      {
        id: "pricing",
        label: "Pricing",
        icon: SubscriptionsOutlined,
        path: "pricing",
      },
      {
        id: "leads-users-section",
        label: "LEADS & USERS",
        section: "LEADS", // & USERS
      },
      {
        id: "live-leads",
        label: "Live Leads",
        icon: FiUsers,
        path: "live-leads",
        isLocable: liveLeads,
        lockPath: "/pricing",
      },
      {
        id: "premier-agent-website",
        label: "Investor Website",
        icon: FiGlobe,
        path: "premier-agent-website",
        isLocable: premierAgentWebsite,
        lockPath: "/pricing",
      },
      {
        id: "list-mgmt",
        label: "Investor & Agent List",
        icon: FiBriefcase,
        path: "investor-agent-list",
      },
      // {
      //   id: "investor-leads",
      //   label: "Investor List",
      //   icon: AttachMoneyIcon,
      //   path: "investor-leads",
      // },
      // {
      //   id: "agent-leads",
      //   label: "Agent List",
      //   icon: WorkIcon,
      //   path: "agent-leads",
      // },
      {
        id: "training-resources-section",
        label: "TRAINING RESOURCES",
        section: "TRAINING RESOURCES",
      },
      {
        id: "bpo-homes-training",
        label: "BPO Homes Training Center",
        icon: OpenBookIcon,
        // path: "homes-training-center",
        isLocable:
          type === "realty_agent" &&
          (premierAgentWebsite || dealAnalyzer || liveLeads || bpoFarm),
        handleClick: () =>
          handleTraining(
            type === "realty_agent" &&
              (premierAgentWebsite || dealAnalyzer || liveLeads || bpoFarm)
          ),
        lockPath: "/pricing",
      },
      {
        id: "bpo-tech-training",
        label: "BPO Tech Training Center",
        icon: LaptopIcon,
        // path: "tech-training-center",
        condition: type !== "realty_agent",
        isLocable: premierAgentWebsite || dealAnalyzer || liveLeads || bpoFarm,
        handleClick: () =>
          handleTraining(
            premierAgentWebsite || dealAnalyzer || liveLeads || bpoFarm
          ),
        lockPath: "/pricing",
      },
      {
        id: "settings",
        label: "Settings",
        icon: SettingsIcon,
        path: "settings",
        section: "bottom",
      },
      {
        id: "support",
        label: "Support",
        icon: PersonSupportIcon,
        path: "support-center",
        section: "bottom",
      },
      {
        id: "bpo-tools-section",
        label: "BPO TOOLS",
        section: "BPO TOOLS",
      },
      {
        id: "bpo-report",
        label: "BPO Report",
        icon: FiFileText, // DocumentIcon,
        path: "bpo-report",
      },
      {
        id: "deal-analyzer",
        label: "Deal Analyzer",
        icon: FiBarChart2, // BarChartIcon
        isCollapsible: true,
        subItems: [
          {
            id: "request-pro-forma-report",
            label: "Request Pro Forma Report",
            path: "analyze-deal",
          },
          {
            id: "uncompleted-reports",
            label: "Uncompleted Reports",
            path: "dealanalyzer-reports/uncompleted-reports",
          },
          {
            id: "completed-reports",
            label: "Completed Reports",
            path: "dealanalyzer-reports/completed-reports",
          },
        ],
      },
      {
        id: "agent-ordering-page",
        label: "My Ordering Page",
        icon: FiShoppingCart, // ShoppingCartIcon
        path: "agent-ordering-page",
      },
      {
        id: "services-section",
        label: "SERVICES",
        section: "BPO Homes Realty Tools",
      },
      {
        id: "marketing-center",
        label: "Marketing Center",
        icon: BiBuilding,
        isLocable: premierAgentWebsite || dealAnalyzer || liveLeads || bpoFarm,
        handleClick: handleClick,
        lockPath: "/pricing",
        // lockTitle: "Click to select a plan",
      },
      {
        id: "mda-business-plan",
        label: "MDA Business Plan",
        icon: BadgeIcon,
        isLocable: premierAgentWebsite || dealAnalyzer || liveLeads || bpoFarm,
        handleClick: handleMBAClick,
        lockPath: "/pricing",
        // lockTitle: "Click to select a plan",
      },
      {
        id: "broker-center",
        label: "Broker Center",
        icon: BusinessCenterIcon,
        isLocable: premierAgentWebsite || dealAnalyzer || liveLeads || bpoFarm,
        handleClick: handleBroker,
        lockPath: "/pricing",
        // lockTitle: "Click to select a plan",
      },
      // {
      //   id: "listings-section",
      //   label: "LISTINGS",
      //   section: "LISTINGS",
      // },
      // {
      //   id: "claimed-listings",
      //   label: "Claimed Listings",
      //   icon: PlaylistAddCheckOutlined,
      //   path: "claimed-listings",
      // },
      // {
      //   id: "requested-listings",
      //   label: "Requested Listings",
      //   icon: PlaylistAddOutlined,
      //   path: "requested-listings",
      // },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bpoFarm, dealAnalyzer, liveLeads, premierAgentWebsite, type]);

  const topMenuItems = menuItems.filter((item) => item.section !== "bottom");
  const bottomMenuItems = menuItems.filter((item) => item.section === "bottom");

  const renderMenuItems = (items, level = 0) => {
    return items.map((item) => {
      if (item.section && item.section !== "bottom") {
        return isOpen ? (
          <SectionTitle key={item.id}>{item.section}</SectionTitle>
        ) : (
          <Divider
            sx={{ pt: 1.5, mb: 1.5, bgcolor: "transparent !important" }}
          />
        );
      }

      const ListItemIconComponent = item.icon;
      const isExpanded = item.isCollapsible ? openCollapsible[item.id] : false;

      if (item.condition === false) return null;

      return (
        <React.Fragment key={item.id}>
          <Lockable
            subscribed={item.isLocable ?? true}
            link={item.lockPath ?? ""}
            title={item.lockTitle}
          >
            <NavLink
              to={item.path ?? ""}
              className="nav_link"
              style={{
                cursor: "pointer",
              }}
              onClick={(e) => {
                if (!item.path) e.preventDefault();

                if (item?.handleClick) item.handleClick();
                else {
                  if (item.isLocable !== undefined && item.isLocable === false)
                    navigate(item.lockPath);
                }
                onClose();
              }}
            >
              <StyledListItem
                isopen={isOpen}
                sx={{
                  bgcolor: !!pathname.includes(item.path)
                    ? "secondary.main"
                    : "paper.main",
                  pl: level > 0 ? 3.53 * (level + 1) : "",
                }}
                onClick={() => {
                  if (item.isCollapsible) handleCollapsibleClick(item.id);
                }}
              >
                {ListItemIconComponent && (
                  <Tooltip title={isOpen ? "" : item.label} placement="right">
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: !!pathname.includes(item.path)
                          ? "paper.main"
                          : "neutral.main",
                      }}
                    >
                      <ListItemIconComponent size="24" />
                    </ListItemIcon>
                  </Tooltip>
                )}

                <Box
                  sx={{
                    width: "100%",
                    display: isOpen ? "flex" : "none",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <StyledListItemText
                    primary={item.label}
                    sx={{
                      color: !!pathname.includes(item.path)
                        ? "paper.main" // "primary.menuActive"
                        : "text.main",
                    }}
                  />

                  {item.isLocable === undefined ? (
                    item.isCollapsible &&
                    (isExpanded ? <ExpandLess /> : <ExpandMore />)
                  ) : item.isLocable === false ? (
                    <LockIcon fontSize="16px" sx={{ ml: "auto", mr: "5px" }} />
                  ) : null}
                </Box>
              </StyledListItem>
            </NavLink>
          </Lockable>

          {item.isCollapsible && item.subItems && (
            <Collapse
              in={isExpanded}
              color="neutral.light"
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                {renderMenuItems(item.subItems, level + 1)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  };

  if (type === "agent" || type === "realty_agent" || !type) {
    return (
      <StyledSideNav
        variant="permanent"
        anchor="left"
        sx={{
          width: isOpen ? "22.7%" : "80px",
          // backgroundImage: { xs: "none", md: `url(${sideNavBG})` }, // Discuss this with Design team later on
        }}
      >
        <List disablePadding>{renderMenuItems(topMenuItems)}</List>

        {isOpen && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <SubscriptionInfo />
            <Divider sx={{ my: 1, bgcolor: "border.divider" }} />
          </>
        )}

        <List disablePadding>{renderMenuItems(bottomMenuItems)}</List>
      </StyledSideNav>
    );
  }
};

export default AgentLeftNavNew;
